/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { useHistory } from 'react-router';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { routeTable } from 'src/core/utils/routeTable';
import { CustomTextProjectKind } from 'src/customText/core/stores/CustomTextProjectStore';

export const useInitializeCustomTextProject = ({
    projectId: projectName,
    projectKind
}: {
    projectId: string;
    projectKind: CustomTextProjectKind;
}) => {
    const history = useHistory();
    const { dispatcher, rootStore } = useGlobalState();

    const { currentCustomTextProjectStore, azureStore } = rootStore;
    const currentSelectedCsStore = azureStore.session.csAccountStore;

    const projectTracker = useProgressTracker({
        persistOnComplete: true,
        onComplete: () => dispatcher.dispatch('getCustomTextTrainedModels', { tracker: trainedModelsTracker, toastData: {} })
    });

    const trainedModelsTracker = useProgressTracker({
        onComplete: () => {
            dispatcher.dispatch('pollCustomTextTrainingJobs', { toastData: {} });
            if (projectKind === 'CustomEntityRecognition') {
                dispatcher.dispatch('pollCustomTextAutoTaggingJobs', { toastData: {} });
            }

            if (FeatureToggles.CustomTextGptLabeling) {
                dispatcher.dispatch('fetchGptJobs', { toastData: {} });
                dispatcher.dispatch('fetchGptPredictions', { toastData: {} });
            }
        }
    });

    React.useEffect(() => {
        dispatcher.dispatch('openCustomTextProjectWithBatchLoading', { projectName, toastData: {}, tracker: projectTracker });

        return () => dispatcher.dispatch('closeCustomTextProject');
    }, []);

    React.useEffect(() => {
        if (currentCustomTextProjectStore?.csAccountPath && currentSelectedCsStore?.path !== currentCustomTextProjectStore?.csAccountPath) {
            history.push(
                projectKind === 'CustomEntityRecognition'
                    ? routeTable.customText.extractionProjectsPage()
                    : routeTable.customText.classificationProjectsPage()
            );
        }
        if (currentCustomTextProjectStore?.amlProjectPath) {
            dispatcher.dispatch('getAmlProjectInfo', {});
        }
    }, [currentSelectedCsStore, currentCustomTextProjectStore]);

    return { isLoaded: projectTracker.isCompleted, isFailed: projectTracker.isFailed };
};
